import { auditReports } from '@mi-front/api-clients';
import { useRouter } from '@mi-front/core';
import { useToast } from '@app/core/notifications';
import { ViewLoader } from '@mi-front/ui';
import { Box, Card, CardContent, Grid, Button, Typography } from '@mui/material';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { aodRoutes } from '../../routing/routes';
import { useAodFactory } from '../aod-rules-add/custom/AodFactory';
import { InfoM } from '../aod-rule-view/components/InfoM';

interface Props {
  rule: auditReports.AodRule;
}

interface AodRuleManualRunFormValues {
  description: auditReports.ManualAodRunRequestDto['description'];
  overrideExportConditions: auditReports.ManualAodRunRequestDto['exportConditions'];
}

export function AodRuleManualRunForm({ rule }: Props) {
  const { formatMessage } = useIntl();
  const { history } = useRouter();
  const { notifySuccess, notifyError } = useToast();

  const {
    fields: { ExportConditions },
    apiToValues,
  } = useAodFactory(rule.reportType);

  const values = apiToValues(rule);
  const mutation = auditReports.useTriggerManualRunAodRules();

  const handleSubmit = async (formData: AodRuleManualRunFormValues) => {
    try {
      await mutation.mutateAsync({
        id: rule.id,
        data: {
          description: formData.description,
          exportConditions: formData.overrideExportConditions,
        },
      });

      notifySuccess(
        formatMessage({
          id: 'audit.aod.rules.manualRun.notifications.triggered',
          defaultMessage: 'Ejecución manual iniciada',
        }),
      );

      // Redirect to rule runs list
      history.push(aodRoutes.ruleView.gen(rule.id));
    } catch (error) {
      notifyError(
        formatMessage({
          id: 'audit.aod.rules.manualRun.notifications.error',
          defaultMessage: 'Error al iniciar la ejecución manual',
        }),
      );
      console.error('Error triggering manual run:', error);
    }
  };

  if (!values) {
    return <ViewLoader />;
  }

  return (
    <Box>
      <InfoM
        label={formatMessage({
          id: 'audit.aod.rules.rule',
          defaultMessage: 'Regla',
        })}
      >
        <Link to={aodRoutes.ruleView.gen(rule.id)}>{rule.name}</Link>
      </InfoM>

      <Box mt={3}>
        <FormContainer<AodRuleManualRunFormValues>
          defaultValues={{
            description: '',
            overrideExportConditions: values.exportConditions,
          }}
          onSuccess={handleSubmit}
        >
          <Grid item xs={12} mb={3}>
            <TextFieldElement
              name="description"
              label={formatMessage({
                id: 'audit.aod.exportConditions.description',
                defaultMessage: 'Descripción',
              })}
              helperText={formatMessage({
                id: 'audit.aod.exportConditions.description.placeholder',
                defaultMessage: 'Descripción opcional de esta ejecución manual',
              })}
              multiline
              fullWidth
            />
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                {formatMessage({
                  id: 'audit.aod.rules.manualRun.exportConditions',
                  defaultMessage: 'Condiciones de export',
                })}
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={3}>
                {formatMessage({
                  id: 'audit.aod.rules.manualRun.exportConditionsDescription',
                  defaultMessage:
                    'Modifica las condiciones de export para esta ejecución manual. Los cambios solo se aplicarán a esta ejecución.',
                })}
              </Typography>

              {ExportConditions && <ExportConditions />}
            </CardContent>
          </Card>

          <Box mt={3} display="flex" gap={2}>
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              loading={mutation.isPending}
              loadingPosition="start"
            >
              {formatMessage({
                id: 'audit.aod.rules.manualRun.submit',
                defaultMessage: 'Iniciar ejecución manual',
              })}
            </Button>
          </Box>
        </FormContainer>
      </Box>
    </Box>
  );
}
